<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { LogOut, Sliders, ArrowLeft, HardHat, Clock, Briefcase } from 'lucide-vue-next';
import { useAuthStore } from '../stores/auth';
import {
  Button,
  Avatar,
  ThemeMenu,
  SimpleThemeToggle,
  BrandLogo,
} from './ui';
import LanguageSwitcher from './LanguageSwitcher.vue';
import NotificationBell from './NotificationBell.vue';

interface Props {
  /** Si true, affiche un bouton retour (vers le name de route donne). */
  backTo?: string;
  /** Label optionnel du bouton retour (sinon fallback "Retour"). */
  backLabel?: string;
}
const props = withDefaults(defineProps<Props>(), {
  backTo: '',
  backLabel: '',
});

const router = useRouter();
const auth = useAuthStore();
const { t } = useI18n();

const brandName = computed(
  () =>
    auth.tenant?.theme?.brand_name
    || import.meta.env.VITE_APP_BRAND_NAME
    || t('common.app.brand_fallback'),
);

const canEditBranding = computed(() => auth.user?.role === 'super_admin_sds');
const isDemoMode = import.meta.env.VITE_DEMO_MODE === 'true';

function goBranding() {
  router.push({ name: 'sds-branding' });
}

function goHome() {
  router.push({ name: 'home' });
}

function goChantiers() {
  router.push({ name: 'chantiers-list' });
}

/**
 * Bouton "Temps" unifie (Pointages + Fiches d'heures).
 * Tous les roles (compagnon inclus) vont sur /temps. TempsView detecte
 * automatiquement le role et affiche soit PointageView (compagnon) soit
 * PointagesListView (autres) dans le tab "Pointages".
 */
function goTemps() {
  router.push({ name: 'temps' });
}

function goGestion() {
  router.push({ name: 'gestion' });
}

/**
 * Bouton "Gestion" unifie regroupant Demandes RH + Vehicules + Paie.
 * Tous les roles operationnels y ont acces (le sous-tab Paie est filtre
 * automatiquement dans GestionView selon le role).
 */
const canSeeGestion = computed(() => {
  const r = auth.user?.role;
  return r === 'super_admin_sds' || r === 'admin' || r === 'direction'
      || r === 'conducteur' || r === 'chef' || r === 'compagnon';
});

/**
 * Le bouton "Temps" est visible pour tous les roles operationnels.
 * Cache pour architecte/client_final qui n'ont pas acces aux pointages
 * ni aux fiches d'heures (cf PermissionChecker).
 */
const canSeeTemps = computed(() => {
  const r = auth.user?.role;
  return r === 'super_admin_sds' || r === 'admin' || r === 'direction'
      || r === 'conducteur' || r === 'chef' || r === 'compagnon';
});

// canSeeDemandes + canSeeVehicules + canSeePaieSettings retires :
// remplaces par canSeeGestion (onglet unifie qui couvre les 3).

function goBack() {
  if (props.backTo) {
    router.push({ name: props.backTo });
  } else {
    router.back();
  }
}

function logout() {
  auth.logout();
  router.push({ name: 'login' });
}
</script>

<template>
  <header class="bg-card border-b border-border sticky top-0 z-sticky">
    <div class="max-w-6xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2 sm:gap-4">
      <div class="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
        <Button v-if="backTo" variant="ghost" size="sm" @click="goBack" :aria-label="backLabel || t('common.common.back')">
          <ArrowLeft :size="14" aria-hidden="true" />
          <span class="hidden lg:inline">{{ backLabel || t('common.common.back') }}</span>
        </Button>
        <button
          type="button"
          @click="goHome"
          class="flex items-center gap-2 sm:gap-3 min-w-0 hover:opacity-80 transition-opacity"
          :aria-label="t('common.nav.menu')"
        >
          <BrandLogo
            :alt="brandName"
            :logo-src="auth.tenant?.theme?.logo_url || undefined"
            size="md"
          />
          <div class="flex flex-col leading-tight min-w-0 text-left">
            <!-- Brand : pas de truncate (Faycal veut "Stafflow" en entier).
                 Le nom tenant reste visible sur tablette+ avec ellipsis si trop long. -->
            <span class="text-sm font-semibold font-display text-text whitespace-nowrap">{{ brandName }}</span>
            <span class="text-xs text-text-muted truncate hidden sm:block">{{ auth.tenant?.name }}</span>
          </div>
        </button>
      </div>
      <div class="flex items-center gap-1 sm:gap-2 shrink-0">
        <!-- Liens nav principaux : visibles UNIQUEMENT sur desktop (>= lg).
             Sur mobile/tablette, c'est la BottomNav fixe qui prend le relais. -->
        <Button v-if="!$route.name?.toString().startsWith('chantier')" variant="ghost" size="sm" class="hidden lg:inline-flex" @click="goChantiers" :aria-label="t('chantiers.page_title')">
          <HardHat :size="14" aria-hidden="true" />
          <span>{{ t('chantiers.page_title') }}</span>
        </Button>
        <Button v-if="canSeeTemps && $route.name !== 'temps'" variant="ghost" size="sm" class="hidden lg:inline-flex" @click="goTemps" :aria-label="t('temps.page_title')">
          <Clock :size="14" aria-hidden="true" />
          <span>{{ t('temps.page_title') }}</span>
        </Button>
        <Button v-if="canSeeGestion && $route.name !== 'gestion' && !$route.name?.toString().startsWith('vehicule') && $route.name !== 'qui-conduisait'" variant="ghost" size="sm" class="hidden lg:inline-flex" @click="goGestion" :aria-label="t('gestion.page_title')">
          <Briefcase :size="14" aria-hidden="true" />
          <span>{{ t('gestion.page_title') }}</span>
        </Button>
        <Button v-if="canEditBranding" variant="ghost" size="sm" class="hidden lg:inline-flex" @click="goBranding" aria-label="Branding">
          <Sliders :size="14" aria-hidden="true" />
          <span>Branding</span>
        </Button>
        <!-- Actions secondaires.
             Sur mobile, on garde seulement Notif + Avatar pour laisser respirer
             le nom du tenant. LanguageSwitcher et ThemeMenu repassent en md+.
             En mode démo, le bouton logout du header est cache : la DemoBanner
             a deja un bouton "Quitter la démo" qui redirige proprement vers /demo. -->
        <NotificationBell v-if="auth.user" />
        <!-- Langue + theme : visibles sur TOUTES les tailles (Faycal a
             remonte que sur smartphone admin n'avait pas acces a la
             langue/theme). Les composants sont des dropdowns compacts
             (icone uniquement sur mobile, label visible >= md). -->
        <div class="flex items-center gap-1 sm:gap-2">
          <LanguageSwitcher />
          <ThemeMenu v-if="isDemoMode" />
          <SimpleThemeToggle v-else />
        </div>
        <!-- Avatar visible UNIQUEMENT sur desktop (>= sm). Sur mobile il
             prenait la place qui empechait "Stafflow Pro" de s'afficher
             en entier ("Staffl..." tronque). L'info nom+role reste
             accessible via la home (carte profil) ou un futur menu. -->
        <Avatar v-if="auth.user" :name="auth.user.name" size="sm" class="hidden sm:inline-flex" />
        <Button
          v-if="!auth.isDemo"
          variant="secondary"
          size="sm"
          @click="logout"
          :aria-label="t('common.nav.logout')"
        >
          <LogOut :size="14" aria-hidden="true" />
          <span class="hidden lg:inline">{{ t('common.nav.logout') }}</span>
        </Button>
      </div>
    </div>
  </header>
</template>
